// @ts-check
import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import parseHTMLToReact from "html-react-parser";
import { marked } from "marked";
import { Icon, Flex } from "orcs-design-system";
import {
  GradientBorder,
  Content,
  ToggleButton,
  CollapsibleContent,
  TypingIndicator,
} from "./StyledComponents";
import { AIWarning } from "./AIWarning";
import AIIcon from "./AI_Icon";
import { DASHBOARD_REGEX_V2, E_CHART_REGEX, GRAPH_REGEX } from "./consts";
import { replaceTagWhileStreaming } from "./utils";

/**
 * Parses Markdown text to HTML string
 * @param {string} text - Markdown text to parse
 * @returns {string} HTML string
 */
const parseMDToHTML = (text) => {
  return marked.parse(text, { async: false });
};

/**
 * @typedef {Object} AIMessageProps
 * @property {string | undefined} [content]
 * @property {Object} [options]
 * @property {boolean} [complete]
 * @property {boolean} [showAIWarning]
 * @property {string} [collapsible] - Controls collapsible behavior.
 *   - If not provided: No collapsible functionality (default behavior)
 *   - If "expanded": Shows collapsible button, defaults to expanded state
 *   - If "collapsed": Shows collapsible button, defaults to collapsed state
 * @property {boolean} [isCollapsed]
 * @property {Function} [onReveal]
 * @property {string} [title] - Custom title for the collapsible section. Defaults to "AI Summary"
 */

/**
 * @param {AIMessageProps} props
 * @returns {import("react").JSX.Element | import("react").JSX.Element[]}
 */
export const AIMessage = ({
  content,
  options,
  complete,
  showAIWarning = true,
  collapsible,
  onReveal = () => {},
  title = "AI Summary",
}) => {
  // Handle collapsible state internally
  // Note: collapsible prop controls whether the component shows collapsible functionality
  // - If collapsible is not provided: No collapsible button or behavior
  // - If collapsible="expanded": Shows button, defaults to expanded (visible content)
  // - If collapsible="collapsed": Shows button, defaults to collapsed (hidden content)
  const [isCollapsed, setIsCollapsed] = useState(collapsible === "collapsed");

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      if (prev) {
        if (!content) {
          onReveal();
        }
      }
      return !prev;
    });
  }, [onReveal, content]);

  // Safe HTML parsing with fallback
  const renderContent = () => {
    let outText = content;
    try {
      outText = replaceTagWhileStreaming(
        outText,
        GRAPH_REGEX,
        "graph",
        complete
      );

      outText = replaceTagWhileStreaming(
        outText,
        DASHBOARD_REGEX_V2,
        "dashboard",
        complete
      );
      outText = replaceTagWhileStreaming(
        outText,
        E_CHART_REGEX,
        "e-chart",
        complete
      );

      // Parse HTML directly since stripHTMLTags ensures only complete tags remain
      outText = parseMDToHTML(outText);
      const outReactElements = parseHTMLToReact(outText, options);

      return outReactElements;
    } catch (error) {
      // Return plain text as fallback
      return <span>{content}</span>;
    }
  };

  const hasContent = (content?.length || 0) > 0 || collapsible;

  return (
    (hasContent && (
      <GradientBorder>
        <Content isCollapsed={isCollapsed}>
          {collapsible && (
            <ToggleButton
              onClick={toggleCollapsed}
              isCollapsed={isCollapsed}
              aria-label={isCollapsed ? `Expand ${title}` : `Collapse ${title}`}
            >
              <Flex alignItems="center" gap="s">
                {title && <AIIcon width={20} height={19} />}
                {title}
              </Flex>
              <Icon
                /** @ts-ignore */
                icon={
                  isCollapsed ? ["fas", "chevron-down"] : ["fas", "chevron-up"]
                }
                size="xs"
              />
            </ToggleButton>
          )}
          <CollapsibleContent
            collapsible={collapsible}
            isCollapsed={isCollapsed}
          >
            {/** @type {import("react").JSX.Element[]} */ renderContent()}
            {complete ? "" : <TypingIndicator />}
            {(showAIWarning || (complete && content)) && (
              <Flex justifyContent="space-between">
                {showAIWarning && <AIWarning />}
              </Flex>
            )}
          </CollapsibleContent>
        </Content>
      </GradientBorder>
    )) ||
    /** @type {import("react").JSX.Element[]} */ ([])
  );
};

AIMessage.propTypes = {
  onReveal: PropTypes.func,
  content: PropTypes.string,
  options: PropTypes.any,
  complete: PropTypes.bool,
  showAIWarning: PropTypes.bool,
  collapsible: PropTypes.oneOf(["collapsed", "expanded"]),
  title: PropTypes.string,
};
